import {useContext, useState, useEffect} from 'react';
import TaskList from './task-list';
import TaskForm from './task-form';
import TaskChangelog from './task-changelog';
import {Box, Button, Typography, Tabs, Tab} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import ArchiveIcon from '@mui/icons-material/Archive';
import UserContext from '../../../../Contexts/UserContext/UserContext';
import TaskBoard from './task-board';
import { useSearchParams } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`task-tabpanel-${index}`}
      aria-labelledby={`task-tab-${index}`}
      {...other}>
      {value === index && <Box sx={{py: 3}}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `task-tab-${index}`,
    'aria-controls': `task-tabpanel-${index}`,
  };
}

export default function Tasks() {
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [autoOpenTaskId, setAutoOpenTaskId] = useState<number | null>(null);
  
  const {user_roll} = useContext(UserContext);
  const isAdmin = user_roll?.includes('Administrator');
  const isAdminView = isAdmin || user_roll?.includes('Admin Aufgaben');

  // Check for task parameter in URL on component mount
  useEffect(() => {
    console.log('[DEBUG Tasks.tsx] Component mounted/updated');
    console.log('[DEBUG Tasks.tsx] Current URL:', window.location.href);
    console.log('[DEBUG Tasks.tsx] Search params:', searchParams.toString());
    
    const taskParam = searchParams.get('task');
    console.log('[DEBUG Tasks.tsx] Task parameter from URL:', taskParam);
    
    if (taskParam) {
      const taskId = parseInt(taskParam, 10);
      console.log('[DEBUG Tasks.tsx] Parsed task ID:', taskId, 'isNaN:', isNaN(taskId));
      
      if (!isNaN(taskId)) {
        console.log('[DEBUG Tasks.tsx] Setting autoOpenTaskId to:', taskId);
        setAutoOpenTaskId(taskId);
        
        // Remove the task parameter from URL to keep it clean
        console.log('[DEBUG Tasks.tsx] Cleaning URL - removing task parameter');
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('task');
        setSearchParams(newSearchParams);
        console.log('[DEBUG Tasks.tsx] URL cleaned, new search params:', newSearchParams.toString());
      } else {
        console.log('[DEBUG Tasks.tsx] Invalid task ID format:', taskParam);
      }
    } else {
      console.log('[DEBUG Tasks.tsx] No task parameter found in URL');
    }
  }, [searchParams, setSearchParams]);

  const handleTaskCreated = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAutoOpenComplete = () => {
    console.log('[DEBUG Tasks.tsx] handleAutoOpenComplete called, clearing autoOpenTaskId');
    setAutoOpenTaskId(null);
  };

  return (
    <Box sx={{p: 3}}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}>
        <Typography variant='h4' component='h1'>
          Aufgaben
        </Typography>
        <Button variant='contained' startIcon={<AddIcon />} onClick={() => setIsTaskFormOpen(true)}>
          Neue Aufgabe erstellen
        </Button>
      </Box>

      <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label='task tabs'>
          {isAdminView && <Tab label='Kanban Board' {...a11yProps(0)} />}
          <Tab label='Meine Aufgaben' {...a11yProps(isAdminView ? 1 : 0)} />
          {isAdminView && <Tab label='Alle Aufgaben' {...a11yProps(2)} />}
          {isAdminView && (
            <Tab label='Mein Archiv' iconPosition='start' {...a11yProps(3)} />
          )}
          {isAdminView && (
            <Tab
              label='Changelog'
              icon={<HistoryIcon />}
              iconPosition='start'
              {...a11yProps(4)}
            />
          )}
        </Tabs>
      </Box>

      {/* Tab für Kanban Board (nur für Admins) */}
      {isAdminView && (
        <TabPanel value={tabValue} index={0}>
          <TaskBoard 
            key={`taskboard-${refreshTrigger}`} 
            autoOpenTaskId={autoOpenTaskId}
            onAutoOpenComplete={handleAutoOpenComplete}
          />
        </TabPanel>
      )}

      {/* Tab für Meine Aufgaben */}
      <TabPanel value={tabValue} index={isAdminView ? 1 : 0}>
        <TaskList
          onTaskCreated={handleTaskCreated}
          key={`my-tasks-${refreshTrigger}`}
          showOnlyAssigned={true}
          autoOpenTaskId={autoOpenTaskId}
          onAutoOpenComplete={handleAutoOpenComplete}
        />
      </TabPanel>

      {/* Tab für Alle Aufgaben (nur für Admins) */}
      {isAdminView && (
        <TabPanel value={tabValue} index={2}>
          <TaskList
            onTaskCreated={handleTaskCreated}
            key={`all-tasks-${refreshTrigger}`}
            showOnlyAssigned={false}
            autoOpenTaskId={autoOpenTaskId}
            onAutoOpenComplete={handleAutoOpenComplete}
          />
        </TabPanel>
      )}

      {/* Tab für Archiv (nur für Admins) */}
      {isAdminView && (
        <TabPanel value={tabValue} index={3}>
          <TaskList
            onTaskCreated={handleTaskCreated}
            key={`archive-${refreshTrigger}`}
            showOnlyAssigned={false}
            showArchived={true}
            autoOpenTaskId={autoOpenTaskId}
            onAutoOpenComplete={handleAutoOpenComplete}
          />
        </TabPanel>
      )}

      {/* Tab für Changelog */}
      {isAdminView && (
        <TabPanel value={tabValue} index={4}>
          <TaskChangelog />
        </TabPanel>
      )}
      <TaskForm
        isOpen={isTaskFormOpen}
        onClose={() => setIsTaskFormOpen(false)}
        onTaskCreated={() => {
          setIsTaskFormOpen(false);
          handleTaskCreated();
        }}
      />
    </Box>
  );
}
